import { createContext, useContext, useLayoutEffect, useRef, useState } from 'react';
import _ from 'lodash';
import MultiMap from 'multimap';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import { Link } from '@treams/treams-routes';

import * as graphql from 'generated/graphql';
import ChevronRightSmallIcon from 'packages/kakapo-ui-kit/assets/icons/ChevronRightSmallIcon';

import Modal, { ModalBackground } from 'packages/kakapo-ui-kit/components/modals/Modal';
import ModalHeader, {
	ModalHeaderTitle,
} from 'packages/kakapo-ui-kit/components/modals/ModalHeader';
import { useTranslation } from 'react-i18next';

const Row = styled(Flex)<{
	expanded?: boolean;
	selected?: boolean;
}>`
	${(props) =>
		props.selected
			? `
	background: ${props.theme.colors.primary};
	color: white;
`
			: `
	color: ${props.theme.colors.slateBlue};

	&:nth-child(even) {
		background: ${props.theme.colors.whiteBlueLight};
	}

	svg * {
		fill: ${props.theme.colors.slateBlue};
	}
`}

	svg {
		display: block;

		${(props) => props.expanded && `transform: rotate(90deg);`}
	}
`;

const Anchor = styled(Link)`
	color: ${(props) => props.theme.colors.slateBlue};
	text-decoration: underline;
`;

const Context = createContext({
	link: false,
	selectable: true,
	groups: undefined as unknown as Map<string, graphql.Group>,
	edges: undefined as unknown as MultiMap<string | undefined, string>,
	selection: undefined as unknown as Set<string>,
	expanded: undefined as unknown as Set<string>,
	visible: undefined as unknown as Set<string>,
	values: undefined as unknown as Map<string, false | true | 'indeterminate'>,
	update: undefined as unknown as (ids: string[], checked: boolean) => void,
	expand: undefined as unknown as (ids: string[], checked: boolean) => void,
});

function Checkbox(props: {
	value: false | true | 'indeterminate';
	onChange: (value: boolean) => void;
}) {
	const ref = useRef<HTMLInputElement>(null);

	useLayoutEffect(() => {
		if (!ref.current) {
			return;
		}

		ref.current.indeterminate = props.value === 'indeterminate';
	}, [props.value, !!ref.current]);

	return (
		<input
			style={{ display: 'block' }}
			type="checkbox"
			checked={props.value === true}
			onChange={(event) => {
				if (props.value === false) {
					props.onChange(true);
				} else {
					props.onChange(false);
				}
			}}
			ref={ref}
		/>
	);
}

function Department(props: {
	group: graphql.Group;
	canSelect: (group: graphql.Group) => boolean;
	depth: number;
}) {
	const { t } = useTranslation();

	const context = useContext(Context);
	const value = context.values.get(props.group.id)!;

	const children = context.edges.get(props.group.id) ?? [];

	const [hover, setHover] = useState(false);

	const expanded = context.expanded.has(props.group.id) || !props.canSelect(props.group);

	if (!context.visible.has(props.group.id)) {
		return null;
	}

	return (
		<>
			<Row
				pl={props.depth * 20}
				selected={context.selection.has(props.group.id)}
				expanded={expanded}
				py={1}
				alignItems="center"
				onMouseEnter={() => setHover(true)}
				onMouseLeave={() => setHover(false)}
			>
				<Box mr={1} width={20}>
					{children.length > 0 && (
						<Box
							onClick={() => {
								context.expand([props.group.id], !expanded);
							}}
						>
							<ChevronRightSmallIcon />
						</Box>
					)}
				</Box>
				{context.selectable && (
					<Box mr={2} width={20}>
						{props.canSelect(props.group) && (
							<Checkbox
								value={value}
								onChange={(value) => context.update([props.group.id], value)}
							/>
						)}
					</Box>
				)}
				<Box flexGrow={1} flexShrink={1}>
					{context.link ? (
						<Anchor to={`/settings/departments/${props.group.id}`}>{props.group.name}</Anchor>
					) : (
						props.group.name
					)}
				</Box>
				{context.selectable && hover && expanded && children.length > 0 && (
					<>
						{value === true && (
							<Box
								mr={2}
								style={{ cursor: 'pointer' }}
								onClick={() => {
									context.update(children, false);
								}}
							>
								{t('dashboard.sidebar.filters.scope.modal.deselectUnderlying')}
							</Box>
						)}
						{value === false && (
							<Box
								mr={2}
								style={{ cursor: 'pointer' }}
								onClick={() => {
									context.update(children, true);
								}}
							>
								{t('dashboard.sidebar.filters.scope.modal.selectUnderlying')}
							</Box>
						)}
					</>
				)}
			</Row>
			{expanded && (
				<DepartmentList parentID={props.group.id} canSelect={props.canSelect} depth={props.depth} />
			)}
		</>
	);
}

function DepartmentList(props: {
	parentID?: string;
	canSelect: (group: graphql.Group) => boolean;
	depth: number;
}) {
	const context = useContext(Context);
	const children = context.edges.get(props.parentID) ?? [];

	return (
		<>
			{_.sortBy(children, (id) => context.groups.get(id)!.name).map((id) => (
				<Department
					key={id}
					depth={props.depth + 1}
					canSelect={props.canSelect}
					group={context.groups.get(id)!}
				/>
			))}
		</>
	);
}

export function DashboardDepartmentsSelector(props: {
	groups: graphql.Group[];
	selection: graphql.Group[];
	onChange: (groups: graphql.Group[]) => void;
	canSelect: (group: graphql.Group) => boolean;
	link?: boolean;
	expanded?: boolean;
	selectable?: boolean;
}) {
	const groups = new Map(props.groups.map((group) => [group.id, group]));
	const edges = new MultiMap(props.groups.map((group) => [group.parent?.id, group.id]));
	const [expanded, setExpanded] = useState(
		new Set<string>(props.expanded ? props.groups.map(({ id }) => id) : []),
	);

	const selection = new Set(props.selection.map(({ id }) => id));

	const values = new Map<string, false | true | 'indeterminate'>(
		Array.from(props.selection).map(({ id }) => [id, true]),
	);

	const visible = new Set<string>();

	function recurse(id?: string) {
		const value = values.get(id!) ?? false;

		let canView = id === undefined ? true : props.canSelect(groups.get(id!)!);
		let someChildren = value !== false;
		let allChildren = value === true;

		for (const child of edges.get(id) ?? []) {
			recurse(child);

			const value = values.get(child);
			someChildren ||= value !== false;
			allChildren &&= value === true;

			canView ||= visible.has(child);
		}

		if (allChildren) {
			values.set(id!, true);
		} else if (someChildren) {
			values.set(id!, 'indeterminate');
		} else {
			values.set(id!, false);
		}

		if (canView) {
			visible.add(id!);
		}
	}

	recurse();

	return (
		<Context.Provider
			value={{
				link: props.link ?? false,
				selectable: props.selectable ?? true,
				groups,
				edges,
				selection,
				expanded,
				values,
				visible,
				update: (ids, checked) => {
					const copy = new Set(selection);

					function recurse(id: string) {
						if (checked) {
							if (props.canSelect(groups.get(id)!)) {
								copy.add(id);
							}
						} else {
							copy.delete(id);
						}

						for (const child of edges.get(id) ?? []) {
							recurse(child);
						}
					}

					for (const id of ids) {
						recurse(id);
					}

					props.onChange(Array.from(copy).map((id) => groups.get(id)!));
				},
				expand: (ids, selected) => {
					const copy = new Set(expanded);

					function recurse(id: string) {
						if (!selected) {
							copy.delete(id);
						}

						for (const child of edges.get(id) ?? []) {
							recurse(child);
						}
					}

					for (const id of ids) {
						if (selected) {
							copy.add(id);
						}

						recurse(id);
					}

					setExpanded(copy);
				},
			}}
		>
			<DepartmentList depth={0} canSelect={props.canSelect} />
		</Context.Provider>
	);
}

export default function DashboardDepartmentSelectorModal(props: {
	groups: graphql.Group[];
	selection: graphql.Group[];
	onChange: (groups: graphql.Group[]) => void;
	canSelect: (group: graphql.Group) => boolean;
	onDismiss: () => void;
}) {
	const { t } = useTranslation();

	return (
		<ModalBackground>
			<Modal>
				<ModalHeader onCancel={props.onDismiss}>
					<ModalHeaderTitle>{t('dashboard.sidebar.filters.scope.modal.title')}</ModalHeaderTitle>
				</ModalHeader>
				<Box maxHeight="calc(100vh - 120px - 68px)" overflow="scroll">
					<DashboardDepartmentsSelector
						selection={props.selection}
						onChange={props.onChange}
						canSelect={props.canSelect}
						groups={props.groups}
					/>
				</Box>
			</Modal>
		</ModalBackground>
	);
}
